import React from 'react';

export interface MultipleChoiceAnswerState {
  selectedOption: string | number | null;
  isCorrect?: boolean;
  isSubmitted: boolean;
}

interface MultipleChoiceProps {
  options: (string | number)[];
  answerState?: MultipleChoiceAnswerState;
  correctAnswer?: string | number;
  onSelectionChange?: (option: string | number) => void;
  columns?: 2 | 3 | 4;
}

const MultipleChoice: React.FC<MultipleChoiceProps> = ({
  options,
  answerState = { selectedOption: null, isSubmitted: false },
  correctAnswer,
  onSelectionChange,
  columns = 3,
}) => {
  const isInteractiveAndNotSubmitted = !answerState.isSubmitted;

  const handleOptionClick = (option: string | number) => {
    if (!isInteractiveAndNotSubmitted || !onSelectionChange) return;
    onSelectionChange(option);
  };

  const getOptionStyle = (option: string | number) => {
    const isSelected = answerState.selectedOption === option;
    const isCorrectOption = correctAnswer === option;

    if (answerState.isSubmitted && isSelected) {
      if (isCorrectOption) {
        return {
          background: 'rgba(16, 185, 129, 0.2)',
          border: '2px solid #10b981',
          color: '#10b981',
        };
      } else {
        return {
          background: 'rgba(239, 68, 68, 0.2)',
          border: '2px solid #ef4444',
          color: '#ef4444',
        };
      }
    }

    if (answerState.isSubmitted && isCorrectOption && !isSelected) {
      return {
        background: 'rgba(16, 185, 129, 0.1)',
        border: '2px solid rgba(16, 185, 129, 0.5)',
        color: '#10b981',
      };
    }

    if (isSelected) {
      return {
        background: 'rgba(14, 165, 233, 0.2)',
        border: '2px solid #0ea5e9',
        color: '#0ea5e9',
      };
    }

    return {
      background: 'rgba(255, 255, 255, 0.05)',
      border: '2px solid rgba(255, 255, 255, 0.1)',
      color: '#94a3b8',
    };
  };

  const gridCols = {
    2: 'grid-cols-2',
    3: 'grid-cols-3',
    4: 'grid-cols-4',
  };

  return (
    <div className={`grid ${gridCols[columns]} gap-3`}>
      {options.map((option, index) => {
        const style = getOptionStyle(option);
        const isSelected = answerState.selectedOption === option;
        const isCorrectOption = correctAnswer === option;

        return (
          <button
            key={index}
            onClick={() => handleOptionClick(option)}
            disabled={answerState.isSubmitted}
            className={`
              relative p-4 rounded-xl text-center font-semibold text-lg
              transition-all duration-200
              ${isInteractiveAndNotSubmitted ? 'hover:scale-105 cursor-pointer' : 'cursor-default'}
            `}
            style={style}
          >
            {option}

            {/* Indicator icon */}
            {answerState.isSubmitted && isSelected && (
              <div
                className="absolute -top-2 -right-2 w-6 h-6 rounded-full flex items-center justify-center text-white text-xs font-bold"
                style={{
                  backgroundColor: isCorrectOption ? '#10b981' : '#ef4444',
                }}
              >
                {isCorrectOption ? '✓' : '✗'}
              </div>
            )}
          </button>
        );
      })}
    </div>
  );
};

export default MultipleChoice;
